import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { InventoryUntestedLotsDialog } from "../../../../components"
import { useAdjustmentLotSelection, useCreateQuantityAdjustment } from "../../hooks"
import {
  type QuantityAdjustmentFormValues,
  createAdjustmentNewQuantitySchema,
  quantityAdjustmentFormSchema,
} from "../../schemas"
import { toQuantityAdjustmentCreateInput } from "../../utils"
import { AdjustmentLotSelection } from "../AdjustmentLotSelection"
import { AdjustmentTypeHeader } from "../AdjustmentTypeHeader"

import { QuantityAdjustmentFormDetails } from "./QuantityAdjustmentFormDetails"
import { QuantityAdjustmentFormLots } from "./QuantityAdjustmentFormLots"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { InventoryAdjustmentType, type InventoryByProductWarehouse, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface QuantityAdjustmentFormDialogProps {
  product: Product
  warehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onClose: () => void
  onSuccess: () => void
}

export const QuantityAdjustmentFormDialog: FC<QuantityAdjustmentFormDialogProps> = ({
  product,
  warehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onClose,
  onSuccess,
}) => {
  const [quantityByLotId, setQuantityByLotId] = useState<Record<string, number | null>>({})
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const form = useForm<QuantityAdjustmentFormValues>({
    resolver: zodResolver(quantityAdjustmentFormSchema),
    defaultValues: {
      warehouseAreaId,
      adjustmentReason: undefined,
      comment: "",
      document: undefined,
    },
    mode: "onChange",
  })
  const {
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting, errors },
  } = form
  const selectedWarehouseAreaId = watch("warehouseAreaId")

  const { createAdjustment } = useCreateQuantityAdjustment()

  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    untestedLots,
    sortBy,
    sortDirection,
    validateLotsTested,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
    handleUntestedLotsReset,
  } = useAdjustmentLotSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: selectedWarehouseArea!.externalSystemId!,
  })

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  const canSubmit =
    isValid &&
    !isSubmitting &&
    !errors.document &&
    selectedLots.length > 0 &&
    selectedLots.every(
      (lot) =>
        createAdjustmentNewQuantitySchema({ lotQuantity: lot.totalUnits }).safeParse(
          quantityByLotId[lot.rawInventoryId]
        ).success
    )

  const handleQuantityChange = useCallback((lotId: string, value: number | null) => {
    setQuantityByLotId((prev) => ({ ...prev, [lotId]: value }))
  }, [])

  const handleLotRemove = useCallback(
    (lotId: string) => {
      handleSelectionRemove(lotId)
      setQuantityByLotId((prev) => {
        const next = { ...prev }
        delete next[lotId]
        return next
      })
    },
    [handleSelectionRemove]
  )

  const handleWarehouseChange = () => {
    handleSelectionReset()
    setQuantityByLotId({})
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const submitForm = async (formValues: QuantityAdjustmentFormValues) => {
    if (!validateLotsTested()) return

    const isSuccess = await createAdjustment(
      toQuantityAdjustmentCreateInput(formValues, product.productId, quantityByLotId),
      formValues.comment,
      formValues.document
    )

    if (isSuccess) onSuccess()
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedLots.length > 0) {
    return <InventoryUntestedLotsDialog untestedLots={untestedLots} onClose={handleUntestedLotsReset} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='inventory.quantity-adjustment-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleCancel} />
      <FormProvider {...form}>
        <AdjustmentTypeHeader
          adjustmentType={InventoryAdjustmentType.AdjustQuantity}
          product={product}
          warehouseOptions={warehouseOptions}
          onWarehouseChange={handleWarehouseChange}
        />
        <DialogContent className='grid flex-1 grid-cols-[1fr_368px] gap-x-6 overflow-hidden px-10 pb-10 pt-6'>
          <Box className='flex min-w-0 flex-col gap-y-6 overflow-hidden'>
            <AdjustmentLotSelection
              palletGroups={palletGroups}
              isFetching={isFetching}
              selectedLotIds={selectedLotIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              uomName={uomName}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
            />
            <QuantityAdjustmentFormLots
              selectedLots={selectedLots}
              uomName={uomName}
              quantityByLotId={quantityByLotId}
              onQuantityChange={handleQuantityChange}
              onLotRemove={handleLotRemove}
            />
          </Box>
          <QuantityAdjustmentFormDetails />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='inventory.quantity-adjustment-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit(submitForm)}
          data-testid='inventory.quantity-adjustment-form-dialog.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
